import { LogicDeps } from '@chegg-wt/core/state'
import { AppState, FSACreatorPayload } from '@chegg-wt/core/state/types'
import { createLogic } from 'redux-logic'
import { SESSION_STORAGE_KEYS } from '@chegg-wt/core/utils/sessionStorage'
import { setLocal, getLocalBibInfo } from '@chegg-wt/core/utils/localStorage'
import { getBFFError } from '@chegg-wt/core/utils/errors'
import {
  getCreateCitationMetadata,
  getUpdateCitationMetadata,
} from '@chegg-wt/core/utils/plusCiteRioEvent'
import { autociteSources } from '@chegg-wt/core/constants/citationFlow'
import { ActionTypes as UserActionTypes } from '../user/constants'
import {
  CLICKSTREAM_CREATE_CITATION,
  CLICKSTREAM_STATE,
  emitSuccessEvent,
} from '../instrumentation/actions'
import { createProject } from '../currentProject/actions'
import {
  currentProjectIdSelector,
  currentStyleNameSelector,
} from '../currentProject/selectors'
import {
  autociteApiVersionSelector,
  autociteV0RioOptFlagSelector,
} from '../createCitation/selectors'
import {
  currentCitationAutociteRioTrackingIDSelector,
  currentCitationAutociteResponseForRioSelector,
} from './selectors'
import { collectUserStateOnAnomaly } from './helpers'
import {
  citationCreate,
  citationLoaded,
  citationCreateSucceeded,
  citationCreateFailed,
  citationUpdate,
  citationUpdateSucceeded,
  citationUpdateFailed,
  citationCreateInProgress,
  showErrorToastForOneClick,
} from './actions'
import { ActionTypes } from './constants'
import { AddRemoveCitationInProgress } from './types'

export const loadCitationSearchDataLogic = createLogic<AppState, any, any, LogicDeps>({
  type: UserActionTypes.GET_USER,
  process() {
    const searchData = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEYS.searchData))
    const citation = searchData ? searchData.data : {}
    const query = searchData ? searchData.searchQuery : null
    return citationLoaded(
      citation,
      query,
      searchData?.autociteRioTrackingID,
      searchData?.autociteResponseForRio
    )
  },
})

export const citationCreateLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof citationCreate>,
  any,
  LogicDeps
>({
  type: [citationCreate],
  validate({ getState, action }, allow, reject) {
    const projectId = currentProjectIdSelector(getState())

    // There is a high incidence of citation creation error when projectId is not defined
    if (projectId === null || projectId === undefined) {
      collectUserStateOnAnomaly('create', getState())
    }
    const currentProjectExists = !!projectId
    const isCurrentProjectValid = currentProjectExists

    if (isCurrentProjectValid) {
      allow(action)
    } else {
      reject(createProject(action.payload))
    }
  },
  async process({ action, getState, api }, dispatch, done) {
    const {
      citationData: { contributors, ...data },
      annotationText: annotation,
      redirect = true,
    } = action.payload

    // TODO: Loading/Progress Indicators
    // TODO: Collect AutoCite Metrics
    try {
      const projectId = currentProjectIdSelector(getState())
      const style = currentStyleNameSelector(getState())
      const autociteV0RioOptFlag = autociteV0RioOptFlagSelector(getState())
      const autociteApiVersion = autociteApiVersionSelector(getState())
      const result = await api.bff.createCitation({
        projectId,
        contributors,
        annotation,
        data,
      })

      const autociteRioTrackingID = currentCitationAutociteRioTrackingIDSelector(
        getState()
      )
      const autociteResponseForRio = currentCitationAutociteResponseForRioSelector(
        getState()
      )
      const citationMetadata =
        (autociteV0RioOptFlag || autociteApiVersion) && autociteRioTrackingID
          ? getCreateCitationMetadata(
              action.payload.citationData,
              autociteResponseForRio,
              autociteRioTrackingID,
              result.citationId
            )
          : undefined
      dispatch(
        citationCreateSucceeded({
          citation: result,
          navigate: redirect,
          citationMetadata,
        })
      )
      dispatch(
        emitSuccessEvent({
          viewName: CLICKSTREAM_CREATE_CITATION,
          state: CLICKSTREAM_STATE.success,
          style: style?.toLowerCase(),
        })
      )

      // TODO: Hide Loading/Progress Indicators
      // TODO: Collect Form Event Metrics
    } catch (error) {
      const message = getBFFError(error, 'Create Citation Failed')
      dispatch(citationCreateFailed(new Error(message)))
      dispatch(showErrorToastForOneClick(true))

      // TODO: Display Error Message
      // TODO: Hide Loading/Progress Indicators
      // TODO: Collect Form Event Metrics
    } finally {
      dispatch(citationCreateInProgress(-1, AddRemoveCitationInProgress.REMOVE_ALL))
      done()
    }
  },
})

export const citationUpdateLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof citationUpdate>,
  any,
  LogicDeps
>({
  type: [citationUpdate],
  async process({ action, api, getState }, dispatch, done) {
    const {
      initialCitation: { id, projectId },
      citationData: { contributors, ...data },
      annotationText: annotation,
    } = action.payload

    const autociteV0RioOptFlag = autociteV0RioOptFlagSelector(getState())
    const autociteApiVersion = autociteApiVersionSelector(getState())

    // There is a high incidence of citation creation error when projectId is not defined
    if (projectId === null || projectId === undefined) {
      collectUserStateOnAnomaly('update', getState())
    }

    // TODO: Loading/Progress Indicators
    try {
      const updatedCitation = await api.bff.updateCitation({
        citationId: id,
        projectId,
        annotation,
        contributors,
        data,
      })
      const citationMetadata =
        (autociteV0RioOptFlag || autociteApiVersion) &&
        autociteSources.includes(data.source)
          ? getUpdateCitationMetadata(
              action.payload.citationData,
              updatedCitation.citationId
            )
          : undefined
      dispatch(
        citationUpdateSucceeded({
          citation: updatedCitation,
          navigate: true,
          citationMetadata,
        })
      )

      // TODO: Hide Loading/Progress Indicators
      // TODO: Collect Form Event Metrics
    } catch (error) {
      const message = getBFFError(error, 'Save citation failed')
      dispatch(citationUpdateFailed(new Error(message)))

      // TODO: Display Error Message
      // TODO: Hide Loading/Progress Indicators
      // TODO: Collect Form Event Metrics
    } finally {
      done()
    }
  },
})

export const setCitationCount = createLogic<AppState, any, any, LogicDeps>({
  type: ActionTypes.CITATION_CREATE_SUCCEEDED,
  process() {
    const prevCount = getLocalBibInfo().citationCount || 0
    const totalCitations = prevCount + 1
    setLocal('citationCount', totalCitations)
  },
})
